// frontend/src/hooks/useItemMutations.js
//
// Bulk write paths for the Item resource (the catalog rows behind
// ItemSetup) and the Item-create dialog. ItemLocation mutations live
// in useInventoryMutations.js.

import { apiJson } from '../queries/api';
import { DATA } from '../queries/dataChanged';
import { useApiMutation } from '../queries/useApiMutation';


/**
 * Bulk-update items (catalog rows). The endpoint emits 409 with a
 * structured `confirmation_required` body when an edit invalidates
 * pending PO recommendations — the page catches the ApiError, prompts
 * the merchant, and retries with `confirm_po_deletion: true`, which is why
 * the write names the recommendations too.
 *
 * The endpoint has a second gate: a default-supplier swap that adopts a new
 * cost-of-record returns 200 with `requires_confirmation` BEFORE saving. The
 * page surfaces CurrencyChangeConfirmModal, then retries with the modal's
 * `confirm` body fragment ({ confirm_cost_change: true } for this adopt gate),
 * spread into the body — else the retry re-hits the gate and never writes.
 *
 * Variables: { items, confirmPoDeletion, confirm } where items is the bucketed
 * batch and confirm is the modal's confirmation body fragment (or undefined).
 */
export function useItemBulkUpdate() {
  return useApiMutation({
    mutationFn: ({ items, confirmPoDeletion = false, confirm }) => {
      const body = { items, confirm_po_deletion: confirmPoDeletion, ...(confirm || {}) };
      return apiJson.post('/api/items/bulk-update/', body);
    },
    changes: [DATA.items, DATA.supplierLinks, DATA.recommendations],
    edits: ['/api/item-locations/'],
  });
}


/**
 * Create a new item, with its first supplier relation when one is named.
 * Used by the ItemSetup "Add Item" dialog.
 */
export function useCreateItem() {
  return useApiMutation({
    mutationFn: (payload) => apiJson.post('/api/items/', payload),
    changes: [DATA.items, DATA.supplierLinks],
  });
}


/**
 * Create an ItemLocation row (assigns an item to a location). Chained
 * after useCreateItem when the merchant picks a location in the Add
 * Item dialog.
 */
export function useCreateItemLocation() {
  return useApiMutation({
    mutationFn: (payload) => apiJson.post('/api/item-locations/', payload),
    changes: [DATA.inventory],
  });
}
